import { BlotterTable, Box, useBlotterTable, useConstant, type Column, type Order } from '@talos/kyoko';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { memo, useMemo } from 'react';
import { of } from 'rxjs';
import {
  MarketDistributionAveragePriceColumn,
  MarketDistributionAvgPxAllInColumn,
  MarketDistributionFeesColumn,
  MarketDistributionSymbolColumn,
} from '../columns';
import { DistributionWrapper } from '../styles';
import { REMAINDER } from '../types';
import { useChartOptions } from '../useChartOptions';
import { useMarketDistribution } from '../useMarketDistribution';

interface SymbolDistributionChartProps {
  order: Order;
  legIndex?: 0 | 1;
}

export const SymbolDistributionChart = memo(function SymbolDistributionChart({
  order,
  legIndex,
}: SymbolDistributionChartProps) {
  const { distribution } = useMarketDistribution({
    order: order,
    includeZeroCumQty: false,
    legIndex,
    variant: 'Symbol',
  });

  const highchartOptions = useChartOptions({ distribution, centerText: 'Symbol Distribution' });
  const remainderFreeDistribution = useMemo(() => distribution.filter(d => d.id !== REMAINDER), [distribution]);

  const distributionObs = useMemo(
    () => of({ initial: true, data: remainderFreeDistribution }),
    [remainderFreeDistribution]
  );

  const columns = useConstant([
    MarketDistributionSymbolColumn({ pinned: true, showColor: true }),
    {
      type: 'price',
      field: 'cumQty',
      title: 'Qty',
      params: {
        quoteCurrencyField: 'sizeCurrency',
        showDashIfZero: true,
      },
    },
    {
      type: 'percent',
      field: 'y',
      title: 'Pct',
      initialSort: '-',
      params: {
        increment: '0.01',
      },
    },
    MarketDistributionAveragePriceColumn({ tab: 'filled' }),
    MarketDistributionFeesColumn({ showDashIfZero: true }),
    MarketDistributionAvgPxAllInColumn,
  ] satisfies Column[]);

  const blotterTable = useBlotterTable({
    dataObservable: distributionObs,
    errorObservable: undefined,
    columns: columns,
    rowID: 'id',
    gridOptions: {
      domLayout: 'autoHeight',
      onFirstDataRendered: ({ api }) => {
        // Only auto-size columns with synchronously rendered content.
        // Fees and Avg Px All In use InlineFormattedNumber which renders asynchronously,
        // so their widths are set explicitly above to avoid measuring empty cells.
        api.autoSizeColumns(['symbol', 'cumQty', 'y', 'avgPx']);
      },
    },
  });
  if (highchartOptions == null) {
    return null;
  }

  return (
    <DistributionWrapper data-testid="distribution-table">
      <Box h="250px">
        <HighchartsReact highcharts={Highcharts} options={highchartOptions} />
      </Box>
      <Box minHeight="80px" w="100%">
        <BlotterTable rowEndSpacing="spacing12" background="gray.020" {...blotterTable} />
      </Box>
    </DistributionWrapper>
  );
});
